import { Breadcrumb, styled } from '@zeiss/beyond-online-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatches } from 'react-router-dom';

const StyledBreadcrumb = styled(Breadcrumb)`
  > ol {
    padding: 0;
  }
`;

interface BreadcrumbWithComponent {
  component: () => string;
  preserveSearch?: boolean;
  translationKey?: never;
}

interface BreadcrumbWithTranslationKey {
  component?: never;
  preserveSearch?: never;
  translationKey: string;
}

export interface BreadcrumbHandle {
  breadcrumb?: BreadcrumbWithComponent | BreadcrumbWithTranslationKey;
}

/**
 * Breadcrumbs
 *
 * The breadcrumbs are setup within the routes config.
 *
 * - Displays either a `component` or a `translationKey`.
 */
export const Breadcrumbs = () => {
  const { t } = useTranslation(['common']);

  const { search } = useLocation();
  const matches = useMatches();

  const nodes = matches
    .filter(({ handle }) => Boolean((handle as BreadcrumbHandle)?.breadcrumb))
    .map(({ handle, pathname }) => {
      /* istanbul ignore next -- @preserve */
      const {
        component: Component,
        preserveSearch,
        translationKey,
      } = (handle as BreadcrumbHandle)?.breadcrumb ?? {};

      let title: JSX.Element | string = translationKey
        ? `${t(translationKey, { defaultValue: '', ns: 'common' })}`
        : '';

      if (Component) {
        title = <Component />;
      }

      return {
        pathname,
        preserveSearch,
        title,
      };
    });

  return (
    <StyledBreadcrumb
      nodes={nodes.map(({ pathname, preserveSearch, title }) => ({
        /**
         * Query strings are opt-in to not leak React Router's internal ?index.
         *
         * - Example usage: Preserve invitation resource id
         */
        href: `${pathname}${preserveSearch ? search : ''}`,

        // Still type-safe because of Handle type definition
        title: title as string,
      }))}
    />
  );
};
